/**
 * Mints an additional bearer token for an EXISTING credential from the list
 * row.
 *
 * This is the only UI path that can do it: the credential's Secrets
 * association (`ahoy_principal_to_secret`) is `value_read_access: internal`,
 * so the record editor never renders a Secrets row for a credential, and its
 * generic secret-create branch only knows the service-account type anyway.
 *
 * Owned by the always-mounted section (not the row) so a mint survives the
 * row re-rendering or the list refetching mid-flight, and so one modal serves
 * every row.
 */

import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import type { WebhookSecretCreatedData } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';

import { attachSecret, getCredentialName } from './api-access-service';

export interface MintedToken {
  data: WebhookSecretCreatedData;
}

interface MintTokenParams {
  credentialEntityId: string;
  name: string;
}

export interface UseMintTokenResult {
  /** Mint a new token for this credential. No-op while another mint is in flight. */
  mintToken: (credential: CrmObject) => void;
  /** Entity id of the credential currently being minted for, or null. */
  mintingCredentialId: string | null;
  /** The token to reveal once, or null when no reveal is pending. */
  mintedToken: MintedToken | null;
  dismissMintedToken: () => void;
}

export function useMintToken(
  orgId: number,
  onMinted: () => void
): UseMintTokenResult {
  const { user } = useAuthInfo();
  // `user` is null in logged-out / expired-session states; resolve once so the
  // mutation can refuse cleanly instead of dereferencing null.
  const userId: number | null = user?.id ?? null;
  const [mintedToken, setMintedToken] = useState<MintedToken | null>(null);

  const mutation = useMutation({
    mutationFn: async ({
      credentialEntityId,
    }: MintTokenParams): Promise<WebhookSecretCreatedData> => {
      if (userId === null) {
        throw new Error('No authenticated user to mint a token as');
      }
      const { secretEntityId, token, masked_hint } = await attachSecret(
        orgId,
        userId,
        credentialEntityId
      );
      return {
        bearer_token: token,
        secret_hint: masked_hint,
        secret_entity_id: secretEntityId,
      };
    },
    onSuccess: data => {
      // Keep the first reveal if one is somehow reported twice before dismissal.
      setMintedToken(prev => prev ?? { data });
    },
    onError: (err, variables) => {
      logger.error(
        'Failed to mint a token for an existing credential',
        err instanceof Error ? err : new Error(String(err)),
        {
          component: 'crm_events',
          org_id: orgId,
          credential_entity_id: variables.credentialEntityId,
        }
      );
      toast.error(
        `We couldn't create a token for ${variables.name}. Try again.`
      );
    },
    // Refresh either way: a successful mint adds a secret row the record view
    // shows, and a failed attach can still leave a vault row behind.
    onSettled: () => {
      onMinted();
    },
  });

  const { mutate, isPending, variables } = mutation;

  const mintToken = useCallback(
    (credential: CrmObject) => {
      if (isPending) {
        return;
      }
      mutate({
        credentialEntityId: credential.entity_id,
        name: getCredentialName(credential),
      });
    },
    [mutate, isPending]
  );

  const dismissMintedToken = useCallback(() => {
    setMintedToken(null);
  }, []);

  return {
    mintToken,
    mintingCredentialId: isPending
      ? (variables?.credentialEntityId ?? null)
      : null,
    mintedToken,
    dismissMintedToken,
  };
}
